import React, { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Col, Input, Radio, Row } from 'antd'
import { EEntranceOption } from '../../screens/tracking/type'
import { GetDeliveryInfoResponse } from '../../../api/apiTypes'
import { PhoneVerification } from "../../templates/PhoneVerification";
import { useShippingInfo } from "../../../api/useShippingInfo";
import { usePhoneVerification } from "../../../contexts/PhoneVerificationContext";

export const formatPhonePrefix = (phoneNumber?: string) => {
  const digits = phoneNumber?.replace(/[^\d*]/g, '') || ''

  if (digits.length < 5) {
    return ''
  }

  const prefixDigits = digits.slice(0, -4)

  if (prefixDigits.length === 7) {
    return `${prefixDigits.slice(0, 3)}-${prefixDigits.slice(3)}-`
  }

  if (prefixDigits.length === 6) {
    return `${prefixDigits.slice(0, 3)}-${prefixDigits.slice(3)}-`
  }

  if (prefixDigits.length > 3) {
    return `${prefixDigits.slice(0, 3)}-${prefixDigits.slice(3)}-`
  }

  return `${prefixDigits}-`
}

type CodeMode = 'masked' | 'editing' | 'revealed'

type Props = {
  deliveryInfo?: GetDeliveryInfoResponse
  entranceCode?: string
  tracking?: string
  isNight?: boolean
  onEntranceOptionChange: (entranceOption: EEntranceOption) => void
  onEntrancePasswordChange: (entrancePassword: string) => void
}
const EntranceForm = ({ isNight, deliveryInfo, tracking, onEntranceOptionChange, onEntrancePasswordChange }: Props) => {
  const { t } = useTranslation(['location'])
  const [entrance, setEntrance] = useState<EEntranceOption>(deliveryInfo?.entrance_option || (isNight ? EEntranceOption.GATECODE : EEntranceOption.FREE));
  const [entranceCode, setEntranceCode] = useState(deliveryInfo?.entrance_password ?? '');
  const { shipping } = useShippingInfo(tracking);
  const { isTrackingVerified } = usePhoneVerification();
  const phonePrefix = formatPhonePrefix(shipping?.mobile_tel)
  const needsPhoneVerification = !!deliveryInfo?.entrance_password && (!shipping || (!!shipping.mobile_tel_last_digit && !isTrackingVerified(tracking)));
  const [codeMode, setCodeMode] = useState<CodeMode>(needsPhoneVerification ? 'masked' : 'revealed');

  const handleEntranceChange = (nextEntrance: EEntranceOption) => {
    if (nextEntrance === EEntranceOption.GATECODE && entrance !== nextEntrance) {
      setEntranceCode(deliveryInfo?.entrance_password ?? '')
      setCodeMode(needsPhoneVerification ? 'masked' : 'revealed')
    }
    setEntrance(nextEntrance)
  }

  useEffect(() => {
    setCodeMode(current => current === 'editing' ? current : needsPhoneVerification ? 'masked' : 'revealed')
  }, [needsPhoneVerification]);

  useEffect(() => {
    if (deliveryInfo) {
      if (deliveryInfo.entrance_option && deliveryInfo.entrance_option in EEntranceOption) {
        setEntrance(deliveryInfo?.entrance_option)
      }
    }
  }, [deliveryInfo])

  useEffect(() => {
    if (codeMode !== 'editing') {
      setEntranceCode(deliveryInfo?.entrance_password ?? '')
    }
  }, [deliveryInfo?.entrance_password, codeMode])

  useEffect(() => {
    onEntranceOptionChange(entrance)
  }, [entrance])

  useEffect(() => {
    onEntrancePasswordChange(entranceCode)
  }, [entranceCode])

  return (
    <Col style={{ width: '100%' }}>
      <div className={'group-title'}>{t('loc.entry.title')}</div>
      <div className={'round-cntr column'}>
        <div className={'delivery-method-inner'}>
          <Radio.Group className={'column'} onChange={e => handleEntranceChange(e.target.value)} value={entrance}>
            <div className={"radio-item col"}>
              <Radio className={'body2'} data-cy={'radioGate'} value={EEntranceOption.GATECODE}>
                <Trans i18nKey={'GATECODE'} ns={'location'}
                       components={{ blue: <span className={'primary'}/> }}/>
              </Radio>
              <Col>
                {isNight && <div className={'small label black45'} data-cy={'radioGate'}>
                  {t('entrance.gatecode.label')}
                </div>}
                {entrance === EEntranceOption.GATECODE && (
                  <Row className={"align-center"} style={{ alignItems: "end" }}>
                    {codeMode !== 'masked' ? <Input
                      key="editable-code"
                      value={entranceCode}
                      maxLength={20}
                      autoFocus
                      autoComplete={'off'}
                      size={'middle'}
                      type="text"
                      className={"input"}
                      style={{ flex: 1, marginRight: 5 }}
                      data-cy={'changeCode'}
                      placeholder={t('loc.entry.code.place')}
                      onChange={e => {
                        setCodeMode('editing')
                        setEntranceCode(e.target.value)
                      }}
                    /> : <Input
                      key="masked-code"
                      value="••••"
                      readOnly
                      type="text"
                      size={'middle'}
                      className={"input"}
                      style={{ flex: 1, marginRight: 5 }}
                      data-cy={'changeCode'}
                      aria-label={t('loc.entry.code.place')}
                      onFocus={() => {
                        setEntranceCode('')
                        setCodeMode('editing')
                      }}
                    />}
                    {needsPhoneVerification && codeMode === 'masked' &&
                        <PhoneVerification phonePrefix={phonePrefix} lastFourDigits={shipping?.mobile_tel_last_digit}
                                           tracking={tracking}
                                           onVerified={() => setCodeMode('revealed')}/>}
                  </Row>
                )}
              </Col>
            </div>

            <div className={"radio-item col"}>
              <Radio
                value={EEntranceOption.GUARD}
                data-cy={'radioGUARD'}
                className={'body2'}
              >
                {t('GUARD.call')}
              </Radio>
              {isNight && <div className={'small label black45'} data-cy={'radioGate'}>
                {t('entrance.guard.label')}
              </div>}
            </div>

            <Radio value={EEntranceOption.FREE} data-cy={'radioFree'}
                   className={'body2 radio-item'}>
              {t('FREE')}
            </Radio>
          </Radio.Group>
        </div>
      </div>
    </Col>
  )
}

export default EntranceForm
